import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Form, Spinner, Modal, Button, Badge, InputGroup, Tabs, Tab } from 'react-bootstrap';
import {
  FaSearch,
  FaBook,
  FaCheck,
  FaTimes,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaTrash,
  FaUsersCog,
  FaPlus
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';
import { toast } from 'react-toastify';

const getCourseId = (orgCourse) => orgCourse.course_id || orgCourse.course?.id;
const formatDateTimeCell = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const CourseList = () => {
  const { user } = useAuth();
  const orgId = user?.organization_id;

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showLearnerModal, setShowLearnerModal] = useState(false);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);
  const [learnerTab, setLearnerTab] = useState('enrolled');
  const [instructorTab, setInstructorTab] = useState('assigned');

  const [enrolledLearners, setEnrolledLearners] = useState([]);
  const [assignedInstructors, setAssignedInstructors] = useState([]);
  const [allAssignedInstructorIds, setAllAssignedInstructorIds] = useState([]);
  const [allInstructors, setAllInstructors] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [enrollmentType, setEnrollmentType] = useState('group');
  const [orgGroups, setOrgGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupPreview, setGroupPreview] = useState({ toEnroll: [], alreadyEnrolled: [] });
  const [groupPreviewLoading, setGroupPreviewLoading] = useState(false);
  const [enrollSaving, setEnrollSaving] = useState(false);
  const [availableLearners, setAvailableLearners] = useState([]);
  const [selectedLearnerIds, setSelectedLearnerIds] = useState([]);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [individualLoading, setIndividualLoading] = useState(false);
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');

  const [instructorSearch, setInstructorSearch] = useState('');
  const [pickerInstructorIds, setPickerInstructorIds] = useState([]);
  const [instructorSaving, setInstructorSaving] = useState(false);

  useEffect(() => {
    if (orgId) {
      fetchCourses();
    }
  }, [orgId]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.organizations.getCourses(orgId);
      let courseData = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        courseData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        courseData = response.data;
      }
      setCourses(courseData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const filteredCourses = Array.isArray(courses) ? courses.filter((orgCourse) => {
    const course = orgCourse.course || orgCourse;
    const matchesSearch = course.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    switch (filterStatus) {
      case 'active':
        matchesStatus = orgCourse.status === 1 && !orgCourse.deleted;
        break;
      case 'inactive':
        matchesStatus = orgCourse.status === 0 && !orgCourse.deleted;
        break;
      default:
        matchesStatus = !orgCourse.deleted;
        break;
    }

    return matchesSearch && matchesStatus;
  }) : [];

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheck className="w-4 h-4" />
            Active
          </span>
        );
      case 0:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <FaTimes className="w-4 h-4" />
            Inactive
          </span>
        );
      default:
        return <Badge bg="warning">Unknown</Badge>;
    }
  };

  const fetchLearnerEnrollments = async (orgCourse) => {
    const courseId = getCourseId(orgCourse);
    setRosterLoading(true);
    try {
      const response = await api.enrollments.getByCourse(courseId, { orgId });
      const enrollments = response.data?.data || response.data || [];
      setEnrolledLearners(
        Array.isArray(enrollments)
          ? enrollments.filter((enrollment) => !enrollment.deleted)
          : []
      );
    } catch (error) {
      toast.error('Failed to load enrolled learners: ' + (error.response?.data?.message || error.message));
      setEnrolledLearners([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const fetchInstructorData = async (orgCourse) => {
    const courseId = getCourseId(orgCourse);
    setRosterLoading(true);
    try {
      const response = await api.courses.getCourseInstructors(courseId, orgId);
      const payload = response.data?.data || {};
      const instructors = Array.isArray(payload.instructors) ? payload.instructors : [];
      const assignedIds = Array.isArray(payload.assigned_ids) ? payload.assigned_ids : [];
      setAllInstructors(instructors);
      setAllAssignedInstructorIds(assignedIds);
      setPickerInstructorIds(assignedIds);
      setAssignedInstructors(instructors.filter((inst) => assignedIds.includes(inst.id)));
    } catch (error) {
      toast.error('Failed to load instructors: ' + (error.response?.data?.message || error.message));
      setAssignedInstructors([]);
      setAllAssignedInstructorIds([]);
      setAllInstructors([]);
      setPickerInstructorIds([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const loadOrgGroups = async () => {
    try {
      const response = await api.groups.list();
      const allGroups = Array.isArray(response.data?.data) ? response.data.data : [];
      setOrgGroups(allGroups.filter((g) => parseInt(g.organization_id, 10) === parseInt(orgId, 10)));
    } catch (error) {
      toast.error('Failed to load groups: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetEnrollForm = () => {
    setEnrollmentType('group');
    setSelectedGroupId('');
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    setAvailableLearners([]);
    setSelectedLearnerIds([]);
    setLearnerSearch('');
    setScheduleStart('');
    setScheduleEnd('');
  };

  const handleOpenLearnerModal = async (orgCourse) => {
    setActiveCourse(orgCourse);
    setLearnerTab('enrolled');
    resetEnrollForm();
    setShowLearnerModal(true);
    await Promise.all([fetchLearnerEnrollments(orgCourse), loadOrgGroups()]);
  };

  const handleOpenInstructorModal = async (orgCourse) => {
    setActiveCourse(orgCourse);
    setInstructorTab('assigned');
    setInstructorSearch('');
    setShowInstructorModal(true);
    await fetchInstructorData(orgCourse);
  };

  const handleCloseLearnerModal = () => {
    if (actionLoading || enrollSaving) return;
    setShowLearnerModal(false);
    setActiveCourse(null);
    setEnrolledLearners([]);
    resetEnrollForm();
  };

  const handleCloseInstructorModal = () => {
    if (actionLoading || instructorSaving) return;
    setShowInstructorModal(false);
    setActiveCourse(null);
    setAssignedInstructors([]);
    setAllAssignedInstructorIds([]);
    setAllInstructors([]);
    setPickerInstructorIds([]);
    setInstructorSearch('');
  };

  const handleLearnerTabSelect = async (key) => {
    const tab = key || 'enrolled';
    setLearnerTab(tab);

    if (tab === 'enroll' && enrollmentType === 'individual' && activeCourse) {
      try {
        setIndividualLoading(true);
        const response = await api.enrollments.getOrgAvailableLearners(getCourseId(activeCourse), orgId);
        setAvailableLearners(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (error) {
        toast.error('Failed to load learners: ' + (error.response?.data?.message || error.message));
      } finally {
        setIndividualLoading(false);
      }
    }
  };

  const handleEnrollmentTypeChange = async (type) => {
    setEnrollmentType(type);
    setSelectedGroupId('');
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    setSelectedLearnerIds([]);
    setLearnerSearch('');

    if (type === 'individual' && activeCourse) {
      try {
        setIndividualLoading(true);
        const response = await api.enrollments.getOrgAvailableLearners(getCourseId(activeCourse), orgId);
        setAvailableLearners(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (error) {
        toast.error('Failed to load learners: ' + (error.response?.data?.message || error.message));
      } finally {
        setIndividualLoading(false);
      }
    }
  };

  const handleGroupSelect = async (event) => {
    const groupId = event.target.value;
    setSelectedGroupId(groupId);
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    if (!groupId || !activeCourse) return;

    try {
      setGroupPreviewLoading(true);
      const response = await api.enrollments.getOrgGroupPreview(getCourseId(activeCourse), groupId, orgId);
      if (response.data?.success) {
        setGroupPreview(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load preview: ' + (error.response?.data?.message || error.message));
    } finally {
      setGroupPreviewLoading(false);
    }
  };

  const handleToggleLearner = (learnerId) => {
    setSelectedLearnerIds((prev) =>
      prev.includes(learnerId) ? prev.filter((id) => id !== learnerId) : [...prev, learnerId]
    );
  };

  const handleEnrollLearners = async () => {
    if (!activeCourse) return;
    const courseId = getCourseId(activeCourse);
    const schedule = {
      available_from: scheduleStart || null,
      available_to: scheduleEnd || null
    };

    if (!schedule.available_from || !schedule.available_to) {
      toast.error('Please select both schedule start and end date/time.');
      return;
    }

    if (new Date(schedule.available_to) <= new Date(schedule.available_from)) {
      toast.error('Schedule end date/time must be after the start date/time.');
      return;
    }

    if (enrollmentType === 'group') {
      if (!selectedGroupId) {
        toast.error('Please select a group.');
        return;
      }
      try {
        setEnrollSaving(true);
        const response = await api.enrollments.enrollOrgGroup(courseId, selectedGroupId, orgId, schedule);
        const { enrolled, skipped } = response.data;
        toast.success(`${enrolled} user(s) enrolled. ${skipped} already enrolled (skipped).`);
        resetEnrollForm();
        setLearnerTab('enrolled');
        await fetchLearnerEnrollments(activeCourse);
      } catch (error) {
        toast.error('Failed to enroll group: ' + (error.response?.data?.message || error.message));
      } finally {
        setEnrollSaving(false);
      }
      return;
    }

    if (selectedLearnerIds.length === 0) {
      toast.error('Please select at least one learner.');
      return;
    }

    try {
      setEnrollSaving(true);
      const response = await api.enrollments.enrollOrgIndividuals(courseId, selectedLearnerIds, orgId, schedule);
      const { enrolled, skipped } = response.data;
      toast.success(`${enrolled} learner(s) enrolled. ${skipped} skipped (already enrolled or invalid).`);
      resetEnrollForm();
      setLearnerTab('enrolled');
      await fetchLearnerEnrollments(activeCourse);
    } catch (error) {
      toast.error('Failed to enroll learners: ' + (error.response?.data?.message || error.message));
    } finally {
      setEnrollSaving(false);
    }
  };

  const handleDeEnrollLearner = async (enrollment) => {
    const learnerUser = enrollment.User || {};
    const learnerName = `${learnerUser.first_name || ''} ${learnerUser.last_name || ''}`.trim() || 'this learner';

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Unenroll ${learnerName} from this course?`);
    if (!confirmed) return;

    try {
      setActionLoading(`learner-${enrollment.id}`);
      await api.enrollments.delete(enrollment.id);
      toast.success('Learner unenrolled successfully.');
      if (activeCourse) {
        await fetchLearnerEnrollments(activeCourse);
      }
    } catch (error) {
      toast.error('Failed to unenroll learner: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleInstructor = (instructorId) => {
    setPickerInstructorIds((prev) =>
      prev.includes(instructorId) ? prev.filter((id) => id !== instructorId) : [...prev, instructorId]
    );
  };

  const handleSaveInstructors = async () => {
    if (!activeCourse) return;

    try {
      setInstructorSaving(true);
      await api.courses.setCourseInstructors(getCourseId(activeCourse), pickerInstructorIds);
      toast.success('Instructors updated successfully.');
      setInstructorTab('assigned');
      await fetchInstructorData(activeCourse);
    } catch (error) {
      toast.error('Failed to save instructors: ' + (error.response?.data?.message || error.message));
    } finally {
      setInstructorSaving(false);
    }
  };

  const handleRemoveInstructor = async (instructor) => {
    const instructorName = `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim() || instructor.email;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Remove ${instructorName} as instructor for this course?`);
    if (!confirmed || !activeCourse) return;

    try {
      setActionLoading(`instructor-${instructor.id}`);
      const newIds = allAssignedInstructorIds.filter((id) => id !== instructor.id);
      await api.courses.setCourseInstructors(getCourseId(activeCourse), newIds);
      toast.success('Instructor removed successfully.');
      await fetchInstructorData(activeCourse);
    } catch (error) {
      toast.error('Failed to remove instructor: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const filteredAvailableLearners = availableLearners.filter((learner) => {
    if (!learnerSearch.trim()) return true;
    const q = learnerSearch.toLowerCase();
    return (
      `${learner.first_name} ${learner.last_name}`.toLowerCase().includes(q) ||
      learner.email?.toLowerCase().includes(q)
    );
  });

  const filteredInstructorList = allInstructors.filter((inst) => {
    if (!instructorSearch.trim()) return true;
    const q = instructorSearch.toLowerCase();
    return (
      `${inst.first_name} ${inst.last_name}`.toLowerCase().includes(q) ||
      inst.email?.toLowerCase().includes(q)
    );
  });

  const activeCourseName = activeCourse?.course?.name || activeCourse?.course?.title || 'Course';

  return (
    <div className="container-fluid p-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="p-3 sm:p-4 md:p-5">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Manage Courses
          </h5>
        </div>

        <Row className="mb-3 g-2 sm:g-3">
          <Col xs={12} md={6} className="mb-2 mb-md-0">
            <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
              <InputGroup.Text className="bg-white border-0 text-purple-600 px-3 sm:px-3">
                <FaSearch className="w-4 h-4" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search courses by name or description..."
                value={searchTerm}
                onChange={handleSearch}
                className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
              />
            </InputGroup>
          </Col>
          <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
            <Form.Select
              value={filterStatus}
              onChange={handleFilterChange}
              className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
        </Row>

        <Card className="border border-purple-100 bg-white shadow-lg rounded-xl sm:rounded-2xl overflow-hidden">
          <Card.Body className="p-0">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" role="status" className="text-purple-600">
                  <span className="visually-hidden">Loading courses...</span>
                </Spinner>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-6 sm:py-8 bg-white/80 backdrop-blur shadow-md border border-purple-100 rounded-xl sm:rounded-2xl">
                <FaBook className="text-purple-400 mb-3" style={{ fontSize: '2rem' }} />
                <p className="text-gray-600 mb-0 text-sm sm:text-base">
                  {searchTerm ? 'No courses found matching your search.' : 'No courses assigned to your organization yet.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="m-0 min-w-max text-xs sm:text-sm">
                  <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    <tr>
                      <th className="py-2 px-3">Course</th>
                      <th className="py-2 px-3">Duration (mins)</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3 text-end">Enroll Learners</th>
                      <th className="py-2 px-3 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCourses.map((orgCourse, index) => {
                      const course = orgCourse.course || orgCourse;

                      return (
                        <tr key={orgCourse.id || index} className="border-b border-purple-50 hover:bg-purple-50/50 transition-all">
                          <td className="py-2 px-3 align-middle">
                            <div className="d-flex align-items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 d-flex align-items-center justify-content-center text-white shadow-sm">
                                <FaBook className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="fw-semibold text-gray-800 text-sm">{course.name || course.title}</div>
                                <small className="text-muted text-xs">
                                  {course.description
                                    ? (course.description.length > 60
                                      ? `${course.description.substring(0, 60)}...`
                                      : course.description)
                                    : 'No description available'}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                            {course.duration != null ? `${course.duration} mins` : 'N/A'}
                          </td>
                          <td className="py-2 px-3 align-middle">
                            {getStatusBadge(orgCourse.status)}
                          </td>
                          <td className="py-2 px-3 align-middle text-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleOpenLearnerModal(orgCourse)}
                              className="border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition px-3 py-2 text-xs sm:text-sm fw-semibold inline-flex items-center gap-2 ms-auto"
                              title="Enroll learners"
                              aria-label={`Enroll learners for ${course.name || course.title}`}
                            >
                              <FaUserGraduate className="w-4 h-4" />
                              Enroll Course
                            </Button>
                          </td>
                          <td className="py-2 px-3 align-middle">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleOpenInstructorModal(orgCourse)}
                                className="border-0 text-indigo-600 hover:bg-indigo-50 rounded transition p-1"
                                title="Manage instructor assignments"
                                aria-label={`Manage instructor assignments for ${course.name || course.title}`}
                              >
                                <FaChalkboardTeacher className="w-5 h-5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Learner Modal */}
      <Modal
        show={showLearnerModal}
        onHide={handleCloseLearnerModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white d-flex align-items-center gap-2">
            <FaUserGraduate />
            Learners — {activeCourseName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-0">
          <Tabs
            activeKey={learnerTab}
            onSelect={handleLearnerTabSelect}
            className="px-3 pt-3 border-bottom border-purple-100"
          >
            <Tab
              eventKey="enrolled"
              title={
                <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                  <FaUserGraduate />
                  Enrolled
                  <Badge bg="light" text="dark" className="border border-purple-200">{enrolledLearners.length}</Badge>
                </span>
              }
            >
              <div className="p-3 sm:p-4">
                {rosterLoading ? (
                  <div className="text-center py-12">
                    <Spinner animation="border" className="text-purple-600" />
                  </div>
                ) : enrolledLearners.length === 0 ? (
                  <p className="text-center text-sm text-purple-700 py-6 mb-0">
                    No learners enrolled yet. Switch to the Enroll tab to add learners.
                  </p>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-purple-100 shadow-sm bg-white">
                    <table className="m-0 w-full text-xs sm:text-sm">
                      <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <tr>
                          <th className="py-2 px-3 text-left font-bold">Name</th>
                          <th className="py-2 px-3 text-left font-bold">Email</th>
                          <th className="py-2 px-3 text-left font-bold">Enrolled</th>
                          <th className="py-2 px-3 text-left font-bold">Schedule Start</th>
                          <th className="py-2 px-3 text-left font-bold">Schedule End</th>
                          <th className="py-2 px-3 text-left font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrolledLearners.map((enrollment) => {
                          const learnerUser = enrollment.User || {};
                          const loadingKey = `learner-${enrollment.id}`;
                          return (
                            <tr key={enrollment.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                              <td className="py-2 px-3 text-gray-800">{learnerUser.first_name} {learnerUser.last_name}</td>
                              <td className="py-2 px-3 text-gray-600">{learnerUser.email}</td>
                              <td className="py-2 px-3 text-gray-600">
                                {enrollment.enrollment_date
                                  ? new Date(enrollment.enrollment_date).toLocaleDateString()
                                  : '-'}
                              </td>
                              <td className="py-2 px-3 text-gray-600">{formatDateTimeCell(enrollment.available_from)}</td>
                              <td className="py-2 px-3 text-gray-600">{formatDateTimeCell(enrollment.available_to)}</td>
                              <td className="py-2 px-3">
                                <Button
                                  bsPrefix="custom-hamburger"
                                  onClick={() => handleDeEnrollLearner(enrollment)}
                                  disabled={actionLoading === loadingKey}
                                  className="border-0 text-red-600 hover:bg-red-50 rounded transition p-1 disabled:opacity-50"
                                  title="Unenroll learner"
                                  aria-label={`Unenroll ${learnerUser.first_name} ${learnerUser.last_name}`}
                                >
                                  {actionLoading === loadingKey ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <FaTrash className="w-4 h-4" />
                                  )}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Tab>

            <Tab
              eventKey="enroll"
              title={
                <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                  <FaPlus />
                  Enroll
                </span>
              }
            >
              <div className="p-3 sm:p-4">
                <Form.Group className="mb-4">
                  <Form.Label className="font-semibold text-gray-700 text-sm">Enrollment Type</Form.Label>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      id="enroll-type-group"
                      name="enrollmentType"
                      label="By Group"
                      checked={enrollmentType === 'group'}
                      onChange={() => handleEnrollmentTypeChange('group')}
                      disabled={enrollSaving}
                    />
                    <Form.Check
                      type="radio"
                      id="enroll-type-individual"
                      name="enrollmentType"
                      label="Individual Learner"
                      checked={enrollmentType === 'individual'}
                      onChange={() => handleEnrollmentTypeChange('individual')}
                      disabled={enrollSaving}
                    />
                  </div>
                </Form.Group>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700 text-sm">
                      Schedule Start <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={toDateTimeLocalValue(scheduleStart)}
                      onChange={(event) => setScheduleStart(event.target.value)}
                      disabled={enrollSaving}
                      className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700 text-sm">
                      Schedule End <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={toDateTimeLocalValue(scheduleEnd)}
                      onChange={(event) => setScheduleEnd(event.target.value)}
                      disabled={enrollSaving}
                      min={toDateTimeLocalValue(scheduleStart)}
                      className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                    />
                  </Form.Group>
                </div>

                {enrollmentType === 'group' ? (
                  <>
                    <Form.Group className="mb-4">
                      <Form.Label className="font-semibold text-gray-700 text-sm">
                        Select Group <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={selectedGroupId}
                        onChange={handleGroupSelect}
                        disabled={groupPreviewLoading || enrollSaving}
                        className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                      >
                        <option value="">Choose a group linked to this organization...</option>
                        {orgGroups.map((group) => (
                          <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                      </Form.Select>
                      {orgGroups.length === 0 && (
                        <Form.Text className="text-muted text-xs">
                          No groups linked to this organization yet.
                        </Form.Text>
                      )}
                    </Form.Group>

                    {groupPreviewLoading ? (
                      <div className="text-center py-8">
                        <Spinner animation="border" className="text-purple-600" />
                      </div>
                    ) : selectedGroupId ? (
                      <div className="rounded-xl overflow-hidden border border-purple-100">
                        <div className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold d-flex justify-content-between align-items-center">
                          <span><FaCheck className="inline me-1" /> Will be enrolled</span>
                          <span className="bg-white/30 rounded-full px-2 py-0.5 text-xs ms-2">{groupPreview.toEnroll.length}</span>
                        </div>
                        <div className="bg-white px-3 py-2 max-h-44 overflow-y-auto">
                          {groupPreview.toEnroll.length === 0 ? (
                            <p className="text-gray-400 text-xs py-2 text-center mb-0">All group members are already enrolled.</p>
                          ) : (
                            groupPreview.toEnroll.map((u) => (
                              <div key={u.id} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                <FaCheck className="text-green-500 shrink-0" style={{ width: 12 }} />
                                <span className="text-sm text-gray-800">{u.first_name} {u.last_name}</span>
                                <span className="text-xs text-gray-500">({u.email})</span>
                              </div>
                            ))
                          )}
                        </div>
                        {groupPreview.alreadyEnrolled.length > 0 && (
                          <>
                            <div className="px-3 py-2 bg-gray-200 text-gray-600 text-sm font-semibold d-flex justify-content-between align-items-center">
                              <span><FaTimes className="inline me-1" /> Already enrolled (skipped)</span>
                              <span className="bg-gray-400 text-white rounded-full px-2 py-0.5 text-xs ms-2">{groupPreview.alreadyEnrolled.length}</span>
                            </div>
                            <div className="bg-gray-50 px-3 py-2 max-h-32 overflow-y-auto">
                              {groupPreview.alreadyEnrolled.map((u) => (
                                <div key={u.id} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                  <FaTimes className="text-gray-400 shrink-0" style={{ width: 12 }} />
                                  <span className="text-sm text-gray-500 text-decoration-line-through">{u.first_name} {u.last_name}</span>
                                  <span className="text-xs text-gray-400">({u.email})</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <InputGroup className="shadow-sm rounded-lg overflow-hidden bg-white border border-purple-200">
                        <InputGroup.Text className="bg-white border-0 text-purple-500">
                          <FaSearch className="w-4 h-4" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search learners by name or email..."
                          value={learnerSearch}
                          onChange={(e) => setLearnerSearch(e.target.value)}
                          className="border-0 text-sm"
                          aria-label="Search learners"
                        />
                      </InputGroup>
                    </div>

                    {individualLoading ? (
                      <div className="text-center py-8">
                        <Spinner animation="border" className="text-purple-600" />
                      </div>
                    ) : filteredAvailableLearners.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 py-6 mb-0">
                        {availableLearners.length === 0
                          ? 'No learners available for enrollment. All organization learners may already be enrolled.'
                          : 'No learners match your search.'}
                      </p>
                    ) : (
                      <div className="rounded-xl border border-purple-100 bg-white max-h-80 overflow-y-auto p-2">
                        {filteredAvailableLearners.map((learner) => {
                          const isSelected = selectedLearnerIds.includes(learner.id);
                          return (
                            <div
                              key={learner.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleToggleLearner(learner.id)}
                              onKeyDown={(e) => e.key === 'Enter' && handleToggleLearner(learner.id)}
                              aria-label={`Select learner ${learner.first_name} ${learner.last_name}`}
                              className={`d-flex align-items-center gap-3 p-2 rounded-lg mb-1 cursor-pointer transition-all ${
                                isSelected ? 'bg-purple-50 border border-purple-300' : 'hover:bg-gray-50 border border-transparent'
                              }`}
                            >
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleLearner(learner.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Enroll ${learner.first_name} ${learner.last_name}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800">{learner.first_name} {learner.last_name}</div>
                                <div className="text-xs text-gray-500">{learner.email}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedLearnerIds.length > 0 && (
                      <p className="text-sm text-purple-700 mt-2 mb-0">
                        {selectedLearnerIds.length} learner(s) selected for enrollment.
                      </p>
                    )}
                  </>
                )}

                <div className="mt-4 d-flex justify-content-end">
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={handleEnrollLearners}
                    disabled={
                      enrollSaving ||
                      !scheduleStart ||
                      !scheduleEnd ||
                      (enrollmentType === 'group'
                        ? !selectedGroupId || groupPreview.toEnroll.length === 0
                        : selectedLearnerIds.length === 0)
                    }
                    className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 px-4 py-2 disabled:opacity-50"
                  >
                    {enrollSaving ? (
                      <><Spinner animation="border" size="sm" className="me-2" /> Enrolling...</>
                    ) : enrollmentType === 'group' ? (
                      <><FaUsersCog className="w-4 h-4" /> Enroll {groupPreview.toEnroll.length} User(s)</>
                    ) : (
                      <><FaUsersCog className="w-4 h-4" /> Enroll {selectedLearnerIds.length} Learner(s)</>
                    )}
                  </Button>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={12}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseLearnerModal}
                disabled={!!actionLoading || enrollSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Close
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Instructor Modal */}
      <Modal
        show={showInstructorModal}
        onHide={handleCloseInstructorModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white d-flex align-items-center gap-2">
            <FaChalkboardTeacher />
            Instructors — {activeCourseName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-0">
          <Tabs
            activeKey={instructorTab}
            onSelect={(key) => setInstructorTab(key || 'assigned')}
            className="px-3 pt-3 border-bottom border-purple-100"
          >
            <Tab
              eventKey="assigned"
              title={
                <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                  <FaChalkboardTeacher />
                  Assigned
                  <Badge bg="light" text="dark" className="border border-purple-200">{assignedInstructors.length}</Badge>
                </span>
              }
            >
              <div className="p-3 sm:p-4">
                {rosterLoading ? (
                  <div className="text-center py-12">
                    <Spinner animation="border" className="text-purple-600" />
                  </div>
                ) : assignedInstructors.length === 0 ? (
                  <p className="text-center text-sm text-purple-700 py-6 mb-0">
                    No instructors assigned yet. Switch to the Assign tab to add instructors.
                  </p>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-purple-100 shadow-sm bg-white">
                    <table className="m-0 w-full text-xs sm:text-sm">
                      <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <tr>
                          <th className="py-2 px-3 text-left font-bold">Name</th>
                          <th className="py-2 px-3 text-left font-bold">Email</th>
                          <th className="py-2 px-3 text-left font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedInstructors.map((instructor) => {
                          const loadingKey = `instructor-${instructor.id}`;
                          return (
                            <tr key={instructor.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                              <td className="py-2 px-3 text-gray-800">{instructor.first_name} {instructor.last_name}</td>
                              <td className="py-2 px-3 text-gray-600">{instructor.email}</td>
                              <td className="py-2 px-3">
                                <Button
                                  bsPrefix="custom-hamburger"
                                  onClick={() => handleRemoveInstructor(instructor)}
                                  disabled={actionLoading === loadingKey}
                                  className="border-0 text-red-600 hover:bg-red-50 rounded transition p-1 disabled:opacity-50"
                                  title="Remove instructor"
                                  aria-label={`Remove instructor ${instructor.first_name} ${instructor.last_name}`}
                                >
                                  {actionLoading === loadingKey ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <FaTrash className="w-4 h-4" />
                                  )}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Tab>

            <Tab
              eventKey="assign"
              title={
                <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                  <FaPlus />
                  Assign
                </span>
              }
            >
              <div className="p-0">
                {rosterLoading ? (
                  <div className="text-center py-12">
                    <Spinner animation="border" className="text-purple-600" />
                  </div>
                ) : (
                  <>
                    <div className="p-3 border-b border-purple-100 bg-white">
                      <InputGroup className="shadow-sm rounded-lg overflow-hidden bg-white border border-purple-200">
                        <InputGroup.Text className="bg-white border-0 text-purple-500">
                          <FaSearch className="w-4 h-4" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search instructors by name or email..."
                          value={instructorSearch}
                          onChange={(e) => setInstructorSearch(e.target.value)}
                          className="border-0 text-sm"
                          aria-label="Search instructors"
                        />
                      </InputGroup>
                    </div>

                    <div className="d-flex gap-2 px-4 pt-3 pb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        {pickerInstructorIds.length} assigned
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {allInstructors.length - pickerInstructorIds.length} unassigned
                      </span>
                    </div>

                    {filteredInstructorList.length === 0 ? (
                      <div className="text-center py-10 text-gray-500 text-sm">
                        {allInstructors.length === 0
                          ? 'No instructors found for this organization.'
                          : 'No instructors match your search.'}
                      </div>
                    ) : (
                      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                        {filteredInstructorList.map((inst) => {
                          const isAssigned = pickerInstructorIds.includes(inst.id);
                          return (
                            <div
                              key={inst.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleToggleInstructor(inst.id)}
                              onKeyDown={(e) => e.key === 'Enter' && handleToggleInstructor(inst.id)}
                              aria-label={`Toggle instructor ${inst.first_name} ${inst.last_name}`}
                              className={`d-flex align-items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                                isAssigned
                                  ? 'border-purple-500 bg-purple-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'
                              }`}
                            >
                              <div className={`w-9 h-9 rounded-full d-flex align-items-center justify-content-center text-white text-sm fw-bold flex-shrink-0 ${
                                isAssigned ? 'bg-gradient-to-br from-purple-500 to-pink-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'
                              }`}>
                                {inst.first_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm fw-semibold text-gray-800 text-truncate mb-0">
                                  {inst.first_name} {inst.last_name}
                                </p>
                                <p className="text-xs text-gray-500 text-truncate mb-0">{inst.email}</p>
                              </div>
                              <div className={`w-5 h-5 rounded border-2 d-flex align-items-center justify-content-center flex-shrink-0 ${
                                isAssigned ? 'bg-purple-500 border-purple-500' : 'border-gray-300 bg-white'
                              }`}>
                                {isAssigned && (
                                  <FaCheck className="text-white" style={{ width: 10, height: 10 }} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="p-3 border-t border-purple-100">
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={handleSaveInstructors}
                        disabled={instructorSaving || rosterLoading}
                        className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2 disabled:opacity-50"
                      >
                        {instructorSaving ? (
                          <><Spinner animation="border" size="sm" className="me-1" /> Saving...</>
                        ) : (
                          'Save Instructor Assignments'
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={12}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseInstructorModal}
                disabled={!!actionLoading || instructorSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Close
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseList;
